import { FACTOR_KEYS, FACTOR_LABELS, scoreFactors, type Weights } from '@brimstone/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState, Panel, SecondaryButton } from '../../components/ui';
import { useSaveWeights, useTonight, useWeights } from '../../lib/roster';

const SAVE_AFTER_MS = 400;

/**
 * Tonight's weights (SPEC §6.4): a slider per factor, a live preview of the ranking while
 * dragging, saved on the server a moment after the last change.
 */
export function WeightsPanel() {
  const stored = useWeights();
  const tonight = useTonight();
  const save = useSaveWeights();
  const [draft, setDraft] = useState<Weights | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const weights = draft ?? stored.data?.weights ?? null;

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const change = (next: Weights) => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => save.mutate(next), SAVE_AFTER_MS);
  };

  const preview = useMemo(() => {
    if (!weights || !tonight.data) return [];
    return tonight.data.ranked
      .map((c, order) => ({
        name: c.name,
        order,
        score: scoreFactors(tonight.data.factorValues[c.id] ?? [], weights).score,
      }))
      .sort((a, b) => b.score - a.score || a.order - b.order);
  }, [weights, tonight.data]);

  return (
    <section id="weights">
      <Panel
        title="Tonight weights"
        meta={save.isPending ? 'SAVING' : save.isError ? undefined : draft ? 'SAVED' : undefined}
      >
        {!weights ? (
          <EmptyState>{stored.isError ? 'Couldn’t load the weights.' : 'Loading…'}</EmptyState>
        ) : (
          <div className="flex flex-wrap gap-x-8 gap-y-4 px-4 py-4">
            <div className="flex min-w-0 flex-[2_1_280px] flex-col gap-1">
              {FACTOR_KEYS.map((k) => (
                <label
                  key={k}
                  className="grid min-h-11 grid-cols-[120px_minmax(0,1fr)_40px] items-center gap-3"
                >
                  <span className="text-compact">{FACTOR_LABELS[k]}</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={weights[k]}
                    onChange={(e) => change({ ...weights, [k]: Number(e.target.value) })}
                    className="min-h-11 w-full cursor-pointer"
                    aria-label={`${FACTOR_LABELS[k]} weight`}
                  />
                  <span className="text-right font-mono">{weights[k]}</span>
                </label>
              ))}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <SecondaryButton
                  type="button"
                  disabled={!stored.data}
                  onClick={() => stored.data && change(stored.data.defaults)}
                >
                  Reset to defaults
                </SecondaryButton>
                {save.isError && (
                  <span className="text-secondary text-danger">
                    Couldn’t save: {save.error.message}
                  </span>
                )}
              </div>
            </div>
            <div className="min-w-0 flex-[1_1_200px]">
              <div className="text-label uppercase tracking-label text-muted">Preview</div>
              {preview.length === 0 ? (
                <p className="m-0 mt-2 text-secondary text-muted">Nobody in tonight’s pool yet.</p>
              ) : (
                <ol className="m-0 mt-2 flex list-none flex-col gap-1 p-0 text-compact">
                  {preview.map((p, i) => (
                    <li key={p.name} className="flex justify-between gap-3">
                      <span>
                        <span className="font-mono text-muted">
                          {String(i + 1).padStart(2, '0')}
                        </span>{' '}
                        {p.name}
                      </span>
                      <span className="font-mono">{p.score}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </Panel>
    </section>
  );
}
